import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Award, Target, Search, Filter, Download, User, Calendar, DollarSign, TrendingUp, Plus, X, RefreshCw, Database, AlertTriangle, Edit, Trash2, Eye, Globe, Upload, Lock, BarChart3 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useNotification } from '../../contexts/NotificationContext';
import { useSubscription } from '../../hooks/useSubscription';
import { getApiUrl, getAuthHeaders } from "../../config/api";
import axios from 'axios';

// Helper function to add ordinal suffixes to numbers
const addOrdinalSuffix = (num) => {
  if (!num || isNaN(num)) return num;
  
  const j = num % 10;
  const k = num % 100;
  
  if (j === 1 && k !== 11) {
    return num + "st";
  }
  if (j === 2 && k !== 12) {
    return num + "nd";
  }
  if (j === 3 && k !== 13) {
    return num + "rd";
  }
  return num + "th";
};

export default function Results() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { hasFeature, subscription, features } = useSubscription();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSourceType, setFilterSourceType] = useState('all');
  const [sortBy, setSortBy] = useState('show_date');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Manual result form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  
  // Family member state
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedFamilyMemberForResult, setSelectedFamilyMemberForResult] = useState('primary');
  const [newResult, setNewResult] = useState({
    family_member_id: 'primary',
    show: {
      name: '',
      startDate: '',
      endDate: '',
      location: ''
    },
    events: [
      {
        name: '',
        eventDate: '',
        format: '',
        draws: [
          {
            drawNumber: '',
            timerTime: '',
            penaltyTime: '',
            finalTime: '',
            timeStatus: 'OK',
            rank1: '',
            rank2: '',
            contestants: [
              {
                contestantName: '',
                position: '',
                stockName: '',
                points: 0,
                earnings: 0
              }
            ]
          }
        ]
      }
    ],
    totalPoints: 0,
    totalEarnings: 0
  });

  // Import from association state
  const [showImportForm, setShowImportForm] = useState(false);
  const [importData, setImportData] = useState({
    association_name: '',
    results_data: []
  });

  // SQL Server integration state
  const [showSqlServerForm, setShowSqlServerForm] = useState(false);
  const [sqlServerData, setSqlServerData] = useState({
    associationKey: '',
    memberKey: '',
    memberPass: ''
  });
  const [sqlServerResults, setSqlServerResults] = useState([]);
  const [selectedKCIRShow, setSelectedKCIRShow] = useState(null);
  const [kcirShows, setKcirShows] = useState([]);
  const [kcirShowsLoading, setKcirShowsLoading] = useState(false);
  const [showKCIRShowsModal, setShowKCIRShowsModal] = useState(false);
  const [sqlServerLoading, setSqlServerLoading] = useState(false);
  
  

  // Family associations state
  const [familyAssociations, setFamilyAssociations] = useState([]);
  const [selectedFamilyMemberId, setSelectedFamilyMemberId] = useState('');


  // Load settings for pre-filling import form
  useEffect(() => {
    loadSettingsForImport();
  }, []);


  const loadSettingsForImport = async () => {
    try {
      const { data, status } = await axios.get(getApiUrl('/api/user-associations'));
      if (status === 200 && Array.isArray(data)) {
        const mapped = data.map(assoc => ({
          id: assoc.id,                      // unique association ID
          familyMemberId: assoc.family_member_id,  // family member ID (null for current user)
          familyMemberName: assoc.name || '',
          associationKey: assoc.association_key || '',
          associationName: assoc.association_name || '',
          userId: assoc.user_id_at_association || '',
          userPassword: assoc.user_password_at_association || '',
        }));
  
        setFamilyAssociations(mapped);
  
        // Auto-select the FIRST association if present
//        const first = mapped[0];
//        if (first) {
//          // If you have a dropdown elsewhere:
//          setSelectedAssociation?.(first.associationKey); // safe-optional if exists
//  
//          // If this view needs to prefill SQL params:
//          setSqlServerData({
//            associationKey: first.associationKey,
//            memberKey: first.userId,
//            memberPass: first.userPassword,
//          });
//  
//          // If you want to auto-run the fetch based on those params, do it here:
//          // fetchSqlData(first.associationKey, first.userId, first.userPassword);
//        } else {
//          // Optional: clear selections when no data
//          setSelectedAssociation?.('');
//          setSqlServerData({ associationKey: '', memberKey: '', memberPass: '' });
//        }
      }
    } catch (error) {
      console.error('Error loading associations for import:', error);
      // Optional: set an error state here
    }
  };
  
  useEffect(() => {
    if (familyAssociations.length > 0) {
      const first = familyAssociations[0];
      setSelectedFamilyMemberId(String(first.id));
      setSqlServerData({
        associationKey: first.associationKey || '',
        memberKey: first.userId || '',
        memberPass: first.userPassword || '',
      });
    } else {
      setSelectedFamilyMemberId('');
      setSqlServerData({ associationKey: '', memberKey: '', memberPass: '' });
    }
  }, [familyAssociations]);

  const handleFamilyMemberChange = (id) => {
    setSelectedFamilyMemberId(id);
    const a = familyAssociations.find(x => String(x.id) === String(id)); // robust to number/string
    if (a) {
      setSqlServerData({
        associationKey: a.associationKey || '',
        memberKey: a.userId || '',
        memberPass: a.userPassword || '',
      });
    }
  };
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingResult, setDeletingResult] = useState(null);

  useEffect(() => {
    fetchResults();
  }, []);

  // Load family members when subscription data is available
  useEffect(() => {

    if (hasFeature('family_plan')) {
      loadFamilyMembers();
    }
  }, [subscription, features, hasFeature]);

  
  // Calculate total points and earnings
  useEffect(() => {
    const totalPoints = newResult.events.reduce((sum, event) => {
      return sum + event.draws.reduce((drawSum, draw) => {
        return drawSum + draw.contestants.reduce((contestantSum, contestant) => {
          return contestantSum + (parseInt(contestant.points) || 0);
        }, 0);
      }, 0);
    }, 0);
    
    const totalEarnings = newResult.events.reduce((sum, event) => {
      return sum + event.draws.reduce((drawSum, draw) => {
        return drawSum + draw.contestants.reduce((contestantSum, contestant) => {
          return contestantSum + (parseFloat(contestant.earnings) || 0);
        }, 0);
      }, 0);
    }, 0);
    
    setNewResult(prev => ({
      ...prev,
      totalPoints,
      totalEarnings
    }));
  }, [newResult.events]);

  const fetchResults = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl('/api/results'));

      if (response.status === 200) {
        const data = response.data;
        setResults(data);
        

      } else {
        throw new Error('Failed to fetch results');
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      showNotification('Failed to load results', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadFamilyMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const [userRes, familyRes] = await Promise.all([
        axios.get(getApiUrl('/api/profile'), { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(getApiUrl('/api/settings/family-members'), { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const currentUser = userRes.data;
      const familyMembers = familyRes.data || [];
      
      // Create family members list with current user first
      const members = [
        { id: 'primary', name: currentUser.name || 'Primary User', isCurrentUser: true },
        ...familyMembers.map(member => ({
          id: member.id,
          name: member.name,
          isCurrentUser: false
        }))
      ];
      
      setFamilyMembers(members);
      setSelectedFamilyMemberForResult('primary'); // Default to current user
    } catch (error) {
      console.error('Error loading family members:', error);
    }
  };

  const addNewResult = async () => {
    try {
      setLoading(true);
      const payload = { result_data: newResult };
      const response = await axios.post(getApiUrl('/api/results'), payload);

      if (response && response.status >= 200 && response.status < 300) {
        showNotification('Result added successfully', 'success');
        setShowAddForm(false);
        resetForm();
        fetchResults(); // Refresh the list
      }
    } catch (error) {
      console.error('Error adding result:', error);
      showNotification('Failed to add result', 'error');
    } finally {
      setLoading(false);
    }
  };

  const editResult = (result) => {
    setEditingResult(result);
    
    // Transform existing data into nested draws structure (matching imports)
    const transformEvents = (events) => {
      if (!events || !Array.isArray(events)) {
        return [{
          name: '',
          format: '',
          draws: [{
          drawNumber: '',
            timerTime: '',
            penaltyTime: 0,
          finalTime: '',
          timeStatus: 'OK',
            rank1: '',
            rank2: '',
            eventDrawId: undefined,
            contestants: []
          }]
        }];
      }
      
      return events.map(event => {
        // If already using draws, pass through with safe defaults
        if (Array.isArray(event.draws)) {
          return {
        name: event.name || '',
            format: event.format || '',
            draws: (event.draws || []).map(draw => ({
              drawNumber: draw.drawNumber || draw.draw || '',
              timerTime: draw.timerTime || draw.timer || '',
              penaltyTime: draw.penaltyTime ?? draw.penalty ?? 0,
              finalTime: draw.finalTime || draw.final || draw.timerTime || '',
              timeStatus: draw.timeStatus || draw.status || 'OK',
              rank1: draw.rank1 || '',
              rank2: draw.rank2 || '',
              eventDrawId: draw.eventDrawId,
              contestants: Array.isArray(draw.contestants) ? draw.contestants.map(c => ({
                contestantName: c.contestantName || c.name || '',
                position: c.position || '',
                stockName: c.stockName || c.stockname || c.stock || '',
                points: c.points || 0,
                earnings: c.earnings || 0,
                memberId: c.memberId || c.id || null
              })) : []
            }))
          };
        }

        // Fallback: convert flat event to a single-draw structure
        return {
          name: event.name || '',
          format: event.format || '',
          draws: [{
            drawNumber: event.drawNumber || event.draw || '',
        timerTime: event.timerTime || event.timer || '',
            penaltyTime: event.penaltyTime ?? event.penalty ?? 0,
            finalTime: event.finalTime || event.final || event.timerTime || '',
        timeStatus: event.timeStatus || event.status || 'OK',
            rank1: event.rank1 || '',
            rank2: event.rank2 || '',
            eventDrawId: event.eventDrawId || undefined,
            contestants: Array.isArray(event.contestants) ? event.contestants : []
          }]
        };
      });
    };
    
    setNewResult({
      family_member_id: result.family_member_id || 'primary',
      show: {
        name: result.result_data?.ShowName || '',
        startDate: result.result_data?.ShowDate || '',
        endDate: result.result_data?.ShowDate || '',
        location: result.result_data?.show?.location || ''
      },
      events: transformEvents(result.result_data?.events),
      totalPoints: result.result_data?.total_points || result.result_data?.totalPoints || 0,
      totalEarnings: result.result_data?.total_earnings || result.result_data?.totalEarnings || 0
    });
    setShowEditForm(true);
  };

    const viewResult = (result) => {
    setViewingResult(result);
    
    // Transform existing data to new structure if needed
    const transformEvents = (events) => {
      if (!events || !Array.isArray(events)) {
        return [{
          name: '',
          drawNumber: '',
          timerTime: '',
          penaltyTime: '',
          finalTime: '',
          timeStatus: '',
          points: 0,
          earnings: 0,
          placing: '',
          format: '',
          draws: [],
          contestants: []
        }];
      }
      
      return events.map(event => ({
        name: event.name || '',
        eventDate: event.eventDate || '',
        drawNumber: event.drawNumber || '',
        timerTime: event.timerTime || event.timer || '',
        penaltyTime: event.penaltyTime || event.penalty || '',
        finalTime: event.finalTime || event.final || '',
        timeStatus: event.timeStatus || event.status || 'OK',
        points: event.points || 0,
        earnings: event.earnings || 0,
        placing: event.placing || '',
        format: event.format || '',
        draws: event.draws || [],
        contestants: event.contestants || []
      }));
    };
    
    const newResultData = {
      show: {
        name: result.result_data?.ShowName || '',
        startDate: result.result_data?.ShowDate || '',
        endDate: result.result_data?.show_end_date || '',
        location: result.result_data?.show_location || ''
      },
      events: transformEvents(result.result_data?.events),
      totalPoints: result.result_data?.total_points || 0,
      totalEarnings: result.result_data?.total_earnings || 0
    };
    setNewResult(newResultData);
    setShowViewForm(true);
  };

  const toggleShowExpansion = (showId) => {
    setExpandedShows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(showId)) {
        newSet.delete(showId);
      } else {
        newSet.add(showId);
      }
      return newSet;
    });
  };

  const saveEditedResult = async () => {
    try {
      setLoading(true);
      const payload = { result_data: newResult };
      const response = await axios.put(getApiUrl(`/api/results/${editingResult.id}`), payload);

      if (response && response.status >= 200 && response.status < 300) {
        showNotification('Result updated successfully', 'success');
        setShowEditForm(false);
        setEditingResult(null);
        resetForm();
        fetchResults();
      }
    } catch (error) {
      console.error('Error updating result:', error);
      showNotification('Failed to update result', 'error');
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setShowEditForm(false);
    setEditingResult(null);
    resetForm();
  };

  const closeViewForm = () => {
    setShowViewForm(false);
    setViewingResult(null);
    resetForm();
  };

  const resetImportForm = () => {
    setImportData({
      association_name: '',
      results_data: []
    });
    // Reset SQL Server data and show selection
    setSqlServerData({
      associationKey: '',
      memberKey: '',
      memberPass: '',
      selectedShowId: null
    });
    setSelectedKCIRShow(null);
    setKcirShows([]);
    setSqlServerResults([]);
  };

  const updateResult = async (resultId, updatedData) => {
    try {
      const response = await axios.put(getApiUrl(`/api/results/${resultId}`), updatedData);

      if (response.status === 200) {
        showNotification('Result updated successfully', 'success');
        fetchResults();
      } else {
        throw new Error('Failed to update result');
      }
    } catch (error) {
      console.error('Error updating result:', error);
      showNotification(error.message, 'error');
    }
  };

  const deleteResult = async (resultId) => {
    try {
      setLoading(true);
      const response = await axios.delete(getApiUrl(`/api/results/${resultId}`), {
        headers: getAuthHeaders()
      });

      if (response.status === 200) {
        showNotification('Result deleted successfully', 'success');
        fetchResults();
      } else {
        throw new Error('Failed to delete result');
      }
    } catch (error) {
      console.error('Error deleting result:', error);
      showNotification('Failed to delete result', 'error');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (result) => {
    setDeletingResult(result);
    setShowDeleteConfirm(true);
  };

  const importFromAssociation = async () => {
    try {
      setLoading(true);
      
      // Get the family member ID from the selected association
      const selectedAssociation = familyAssociations.find(a => String(a.id) === String(selectedFamilyMemberId));
      const familyMemberId = selectedAssociation ? selectedAssociation.familyMemberId : null;
      
      const response = await axios.post(getApiUrl('/api/results/import/association'), {
        ...importData,
        familyMemberId: familyMemberId
      });

      if (response.status === 200) {
        showNotification('Results imported successfully', 'success');
        setShowImportForm(false);
        resetImportForm();
        fetchResults();
      } else {
        throw new Error('Failed to import results');
      }
    } catch (error) {
      console.error('Error importing results:', error);
      showNotification('Failed to import results', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSqlServerResults = async () => {
    try {
      setSqlServerLoading(true);
      const params = new URLSearchParams({
        associationKey: sqlServerData.associationKey,
        memberKey: sqlServerData.memberKey,
        memberPass: sqlServerData.memberPass,
        showId: selectedKCIRShow?.showId || ''
      });
      
      const response = await axios.get(`${getApiUrl('/api/sqlserver-results/results')}?${params}`, {
        headers: getAuthHeaders()
      });

      if (response.status === 200) {
        const data = response.data;
        setSqlServerResults(data.results || []);
        // Clear previous selection when new results are fetched
        showNotification(`Found ${data.count} results`, 'success');
      } else {
        const errorData = response.data;
        throw new Error(errorData.error || 'Failed to fetch results');
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      showNotification(error.message, 'error');
    } finally {
      setSqlServerLoading(false);
    }
  };

  const fetchKCIRShowsForResults = async () => {
    try {
      setKcirShowsLoading(true);
      const params = new URLSearchParams({
        associationKey: sqlServerData.associationKey || '',
        memberKey: sqlServerData.memberKey || '',
        memberPass: sqlServerData.memberPass || ''
      });
      const response = await axios.get(`${getApiUrl('/api/sqlserver-results/shows')}?${params.toString()}`);
      
      if (response.status === 200) {
        const data = response.data;
        setKcirShows(data.shows || []);
        setShowKCIRShowsModal(true);
      } else {
        throw new Error('Failed to fetch shows');
      }
    } catch (error) {
      showNotification('Failed to load shows', 'error');
    } finally {
      setKcirShowsLoading(false);
    }
  };

  const deleteSqlServerShow = (showIndex) => {
    setSqlServerResults(prev => prev.filter((_, index) => index !== showIndex));
    showNotification('Show removed', 'success');
  };

  // Selection functions for SQL Server results

  const saveSqlServerResultsToDatabase = async (resultsToSave = null) => {
    try {
      
      setLoading(true);
      let resultsToProcess;
      
      if (resultsToSave) {
        resultsToProcess = resultsToSave;
      } else {
        resultsToProcess = sqlServerResults;
      }
      
      if (resultsToProcess.length === 0) {
        showNotification('No results to save', 'warning');
        return;
      }

      // Debug: Check authentication token
      const token = localStorage.getItem('token');      
      const authHeaders = getAuthHeaders();

      // Get the family member ID from the selected association
      const selectedAssociation = familyAssociations.find(a => String(a.id) === String(selectedFamilyMemberId));
      const familyMemberId = selectedAssociation ? selectedAssociation.familyMemberId : null;
      
      const response = await axios.post(getApiUrl('/api/sqlserver-results/save'), {
        results: resultsToProcess,
        associationKey: sqlServerData.associationKey,
        familyMemberId: familyMemberId
      }, {
        headers: authHeaders
      });

      if (response.status === 200) {
        const result = response.data;
        
        // Show detailed success message
        let message = result.message;
        if (result.summary) {
          const { total, inserted, updated } = result.summary;
          if (inserted > 0 && updated > 0) {
            message = `Successfully processed ${total} results (${inserted} new, ${updated} updated)`;
          } else if (inserted > 0) {
            message = `Successfully imported ${inserted} new results`;
          } else if (updated > 0) {
            message = `Successfully updated ${updated} existing results`;
          }
        }
        
        showNotification(message, 'success');
        setShowSqlServerForm(false);
        setSelectedKCIRShow(null);
        setSqlServerResults([]);
        setSqlServerData({
          associationKey: '',
          memberKey: '',
          memberPass: ''
        });
        fetchResults();
      } else {
        const errorData = response.data;
        throw new Error(errorData.error || 'Failed to save results');
      }
    } catch (error) {
      console.error('Error saving results:', error);
      
      // Enhanced error logging
      if (error.response) {
        console.error('Server response error:', error.response.data);
        console.error('Server status:', error.response.status);
        console.error('Server headers:', error.response.headers);
        
        if (error.response.status === 401) {
          showNotification('Authentication failed. Please log in again.', 'error');
        } else if (error.response.status === 403) {
          showNotification('Access denied. Invalid or expired token.', 'error');
        } else {
          showNotification(error.response.data?.error || 'Failed to save results', 'error');
        }
      } else if (error.request) {
        console.error('Request error:', error.request);
        showNotification('Network error. Please check your connection.', 'error');
      } else {
        showNotification(error.message || 'Failed to save results', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

    const resetForm = () => {
    setNewResult({
      family_member_id: selectedFamilyMemberForResult || 'primary',
      show: {
        name: '',
        startDate: '',
        endDate: '',
        location: ''
      },
      events: [
        {
          name: '',
          eventDate: '',
          format: '',
          draws: [
            {
              drawNumber: '',
              timerTime: '',
              penaltyTime: '',
              finalTime: '',
              timeStatus: 'OK',
              rank1: '',
              rank2: '',
              contestants: [
                {
                  contestantName: '',
                  position: '',
                  stockName: '',
                  points: 0,
                  earnings: 0
                }
              ]
            }
          ]
        }
      ],
      totalPoints: 0,
      totalEarnings: 0
    });
  };

  const addEvent = () => {
    setNewResult(prev => ({
      ...prev,
      events: [...prev.events, {
        name: '',
        eventDate: '',
        format: '',
        draws: [
          {
            drawNumber: '',
            timerTime: '',
            penaltyTime: '',
            finalTime: '',
            timeStatus: 'OK',
            rank1: '',
            rank2: '',
            contestants: [
              {
                contestantName: '',
                position: '',
                stockName: '',
                points: 0,
                earnings: 0
              }
            ]
          }
        ]
      }]
    }));
  };

  const removeEvent = (index) => {
    setNewResult(prev => ({
      ...prev,
      events: prev.events.filter((_, i) => i !== index)
    }));
  };

  const updateEvent = (index, field, value) => {
    setNewResult(prev => ({
      ...prev,
      events: prev.events.map((event, i) => 
        i === index ? { ...event, [field]: value } : event
      )
    }));
  };

  const updateNewResult = (field, value) => {
    setNewResult(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateShow = (field, value) => {
    setNewResult(prev => ({
      ...prev,
      show: {
        ...prev.show,
        [field]: value
      }
    }));
  };

  const updateContestant = (field, value) => {
    setNewResult(prev => ({
      ...prev,
      contestant: {
        ...prev.contestant,
        [field]: value
      }
    }));
  };

  const updateEventContestant = (eventIndex, contestantIndex, field, value) => {
    setNewResult(prev => ({
      ...prev,
      events: prev.events.map((event, i) => 
        i === eventIndex ? {
          ...event,
          contestants: event.contestants.map((contestant, j) => 
            j === contestantIndex ? { ...contestant, [field]: value } : contestant
          )
        } : event
      )
    }));
  };

  const addEventContestant = (eventIndex) => {
    setNewResult(prev => ({
      ...prev,
      events: prev.events.map((event, i) => 
        i === eventIndex ? {
          ...event,
          contestants: [...event.contestants, { name: '', position: '' }]
        } : event
      )
    }));
  };

  const removeEventContestant = (eventIndex, contestantIndex) => {
    setNewResult(prev => ({
      ...prev,
      events: prev.events.map((event, i) => 
        i === eventIndex ? {
          ...event,
          contestants: event.contestants.filter((_, j) => j !== contestantIndex)
        } : event
      )
    }));
  };

  // New helper functions for draws structure
  const addDraw = (eventIndex) => {
    setNewResult(prev => ({
      ...prev,
      events: prev.events.map((event, i) => 
        i === eventIndex ? {
          ...event,
          draws: [...(event.draws || []), {
            drawNumber: '',
            timerTime: '',
            penaltyTime: '',
            finalTime: '',
            timeStatus: 'OK',
            rank1: '',
            rank2: '',
            contestants: [
              {
                contestantName: '',
                position: '',
                stockName: '',
                points: 0,
                earnings: 0
              }
            ]
          }]
        } : event
      )
    }));
  };

  const removeDraw = (eventIndex, drawIndex) => {
    setNewResult(prev => ({
      ...prev,
      events: prev.events.map((event, i) => 
        i === eventIndex ? {
          ...event,
          draws: (event.draws || []).filter((_, j) => j !== drawIndex)
        } : event
      )
    }));
  };

  const updateDraw = (eventIndex, drawIndex, field, value) => {
    setNewResult(prev => ({
      ...prev,
      events: prev.events.map((event, i) => 
        i === eventIndex ? {
          ...event,
          draws: (event.draws || []).map((draw, j) => 
            j === drawIndex ? { ...draw, [field]: value } : draw
          )
        } : event
      )
    }));
  };

  const addDrawContestant = (eventIndex, drawIndex) => {
    setNewResult(prev => ({
      ...prev,
      events: prev.events.map((event, i) => 
        i === eventIndex ? {
          ...event,
          draws: (event.draws || []).map((draw, j) => 
            j === drawIndex ? {
              ...draw,
              contestants: [...(draw.contestants || []), {
                contestantName: '',
                position: '',
                stockName: '',
                points: 0,
                earnings: 0
              }]
            } : draw
          )
        } : event
      )
    }));
  };

  const removeDrawContestant = (eventIndex, drawIndex, contestantIndex) => {
    setNewResult(prev => ({
      ...prev,
      events: prev.events.map((event, i) => 
        i === eventIndex ? {
          ...event,
          draws: (event.draws || []).map((draw, j) => 
            j === drawIndex ? {
              ...draw,
              contestants: (draw.contestants || []).filter((_, k) => k !== contestantIndex)
            } : draw
          )
        } : event
      )
    }));
  };

  const updateDrawContestant = (eventIndex, drawIndex, contestantIndex, field, value) => {
    setNewResult(prev => ({
      ...prev,
      events: prev.events.map((event, i) => 
        i === eventIndex ? {
          ...event,
          draws: (event.draws || []).map((draw, j) => 
            j === drawIndex ? {
              ...draw,
              contestants: (draw.contestants || []).map((contestant, k) => 
                k === contestantIndex ? { ...contestant, [field]: value } : contestant
              )
            } : draw
          )
        } : event
      )
    }));
  };



  const exportResults = () => {
    const csvRows = [['Show', 'Event', 'Format', 'Date', 'Draw', 'Place', 'Time/Score', 'Penalty Time/Score', 'Final Time/Score', 'Time Status', 'Points', 'Earnings', 'Contestants', 'Stock', 'Source']];
    
    results.forEach(result => {
      const showName = result.show_name || '';
      const showDate = result.show_start_date || '';
      
      if (result.result_data?.events && result.result_data.events.length > 0) {
        // New structure: flatten draws from all events
        result.result_data.events.forEach(event => {
                      if (event.draws && Array.isArray(event.draws)) {
              event.draws.forEach(draw => {
                // Calculate total points and earnings from all contestants
                const totalPoints = draw.contestants && Array.isArray(draw.contestants) 
                  ? draw.contestants.reduce((sum, c) => sum + (c.points || 0), 0) 
                  : 0;
                const totalEarnings = draw.contestants && Array.isArray(draw.contestants) 
                  ? draw.contestants.reduce((sum, c) => sum + (c.earnings || 0), 0) 
                  : 0;
                const contestants = draw.contestants ? draw.contestants.map(c => {
                  const contestantInfo = c.contestantName || 'Unknown';
                  const positionInfo = c.position ? ` (${c.position})` : '';
                  const stockInfo = c.stockName ? ` - ${c.stockName}` : '';
                  return `${contestantInfo}${positionInfo}${stockInfo}`;
                }).join('; ') : '';
                
                csvRows.push([
                  showName,
                  event.name || '',
                  event.format || '',
                  showDate,
                  draw.drawNumber || '',
                  draw.rank1 || draw.rank2 || '',
                  draw.timerTime || '',
                  draw.penaltyTime || '',
                  draw.finalTime || '',
                  draw.timeStatus || 'OK',
                  totalPoints,
                  totalEarnings,
                  contestants,
                  '', // Stock names are now included with each contestant
                  result.source_type || ''
                ]);
              });
          } else {
            // Fallback for events without draws structure
                            const contestants = event.contestants ? event.contestants.map(c => {
                  const contestantInfo = c.contestantName || c.name || 'Unknown';
                  const positionInfo = c.position ? ` (${c.position})` : '';
                  const stockInfo = c.stockName ? ` - ${c.stockName}` : '';
                  return `${contestantInfo}${positionInfo}${stockInfo}`;
                }).join('; ') : '';
                
                csvRows.push([
                  showName,
                  event.name || '',
                  event.format || '',
                  showDate,
                  event.drawNumber || event.position || '',
                  event.placing || '',
                  event.timerTime || '',
                  event.penaltyTime || '',
                  event.finalTime || '',
                  event.timeStatus || 'OK',
                  event.points || 0,
                  event.earnings || 0,
                  contestants,
                  result.source_type || ''
                ]);
          }
        });
      } else {
        // Fallback to empty result
        csvRows.push([
          showName,
          '',
          '',
          showDate,
          '',
          '',
          '',
          '',
          '',
          'OK',
          0,
          0,
          '',
          '',
          result.source_type || ''
        ]);
      }
    });

    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `results-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'OK': return 'text-green-600 bg-green-100';
      case 'N/T': return 'text-red-600 bg-red-100';
      default: return 'text-green-600 bg-green-100'; // Default to OK
    }
  };

  const getSourceTypeIcon = (sourceType) => {
    switch (sourceType) {
      case 'manual': return <User className="w-4 h-4" />;
      case 'association': return <Database className="w-4 h-4" />;
      case 'webhook': return <RefreshCw className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  const getRefreshStatusIcon = (refreshStatus) => {
    switch (refreshStatus) {
      case 'current': return null;
      case 'outdated': return <RefreshCw className="w-4 h-4 text-yellow-500" />;
      case 'unavailable': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'error': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default: return null;
    }
  };

  const formatScore = (score) => {
    if (!score) return '-';
    if (score === null || score === undefined) return '-';
    if (score === '') return '-';
    return typeof score === 'number' ? score.toFixed(3) : String(score);
  };

  const calculateFinalScore = (score, penalties) => {
    if (!score) return '-';
    if (score === null || score === undefined) return '-';
    if (score === '') return '-';
    
    const scoreNum = typeof score === 'number' ? score : parseFloat(score);
    const penaltiesNum = typeof penalties === 'number' ? penalties : parseFloat(penalties) || 0;
    
    if (isNaN(scoreNum)) return String(score);
    
    const finalScore = scoreNum - penaltiesNum;
    return finalScore.toFixed(3);
  };

  const getEventCount = (result) => {
    if (result.result_data?.events && Array.isArray(result.result_data.events) && result.result_data.events.length > 0) {
      // New structure: count total draws across all events
      let totalDraws = 0;
      result.result_data.events.forEach(event => {
        if (event.draws && Array.isArray(event.draws)) {
          totalDraws += event.draws.length;
        } else {
          // Fallback for events without draws structure
          totalDraws += 1;
        }
      });
      return totalDraws || 1;
    }
    return 1;
  };

  const getTotalPoints = (result) => {
    return result.total_points || 0;
  };

  const getTotalEarnings = (result) => {
    return result.total_earnings || 0;
  };

  // Helper function to safely format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? 'N/A' : format(date, 'MMM dd, yyyy');
    } catch (e) {
      return 'N/A';
    }
  };

  const getShowDateMs = (result) => {
    const dateString =
      result.show_start_date ||
      result.result_data?.ShowDate ||
      result.show_date;

    if (!dateString) return -Infinity;

    const parsed = Date.parse(dateString);
    if (!Number.isNaN(parsed)) return parsed;

    const fallback = new Date(dateString).getTime();
    return Number.isFinite(fallback) ? fallback : -Infinity;
  };

  const filteredResults = results.filter(result => {
    const matchesSearch = !searchTerm || 
      result.show_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.first_event_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.contestant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      // Search in event names and formats
      (result.result_data?.events && result.result_data.events.some(event => 
        (event.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (event.format || '').toLowerCase().includes(searchTerm.toLowerCase())
      ));
    
    const matchesSourceType = filterSourceType === 'all' || result.source_type === filterSourceType;
    
    return matchesSearch && matchesSourceType;
  });

  const sortedResults = [...filteredResults].sort((a, b) => {
    switch (sortBy) {
      case 'created_at':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'show_date': {
        return getShowDateMs(b) - getShowDateMs(a);
      }
      case 'show_name':
        return (a.show_name || '').localeCompare(b.show_name || '');
      case 'total_points':
        return (b.total_points || 0) - (a.total_points || 0);
      case 'total_earnings':
        return (b.total_earnings || 0) - (a.total_earnings || 0);
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  const [showDisclaimerDetails, setShowDisclaimerDetails] = useState(false);
  const [expandedShows, setExpandedShows] = useState(new Set());
  const [showViewForm, setShowViewForm] = useState(false);
  const [viewingResult, setViewingResult] = useState(null);

  return (
    <div className="space-y-6">
      {/* Disclaimer */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 sm:p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-xs sm:text-sm text-yellow-800">
                <strong>Disclaimer:</strong> 
                <span className="hidden sm:inline"> Imported Results information is provided by the originating association. Accuracy and completeness of this data are the sole responsibility of the association.</span>
                <span className="sm:hidden"> Tap for details</span>
              </p>
            </div>
          </div>
          <button 
            className="sm:hidden p-1 text-yellow-600 hover:text-yellow-800 transition-colors"
            onClick={() => setShowDisclaimerDetails(!showDisclaimerDetails)}
            aria-label="Toggle disclaimer details"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        {/* Mobile expandable details */}
        <div className={`sm:hidden mt-2 pt-2 border-t border-yellow-200 ${showDisclaimerDetails ? 'block' : 'hidden'}`}>
          <p className="text-xs text-yellow-800">
            Imported Results information is provided by the originating association. Accuracy and completeness of this data are the sole responsibility of the association.
          </p>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Trophy className="w-8 h-8 text-black-500" />
            Results
          </h1>
          <p className="text-gray-600">Manage your results</p>
        </div>
        {/* Desktop: Full buttons with text */}
        <div className="hidden sm:flex flex-row gap-2 w-full lg:w-auto justify-center">
          <button
            onClick={() => navigate('/results/reports')}
            className="flex items-center justify-center px-2 py-2 sm:px-4 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            title="Results Reports"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            <span>Results Reports</span>
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center justify-center px-2 py-2 sm:px-4 sm:py-2 bg-[#51380d] text-white rounded-lg hover:bg-[#3d2a0a] transition-colors text-sm"
            title="Add New Result"
          >
            <Plus className="w-4 h-4 mr-2" />
            <span>Add New Result</span>
          </button>
          <button
            onClick={exportResults}
            className="flex items-center justify-center px-2 py-2 sm:px-4 sm:py-2 bg-[#b97a3a] text-white rounded-lg hover:bg-[#c98540] transition-colors text-sm"
            title="Export CSV"
          >
            <Download className="w-4 h-4 mr-2" />
            <span>Export CSV</span>
          </button>
        </div>
                </div>

      {/* Mobile: Icons only in horizontal row - centered */}
      <div className="sm:hidden flex justify-center w-full mt-4">
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/results/reports')}
            className="flex items-center justify-center p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            title="Results Reports"
          >
            <BarChart3 className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center justify-center p-3 bg-[#51380d] text-white rounded-lg hover:bg-[#3d2a0a] transition-colors"
            title="Add New Result"
          >
            <Plus className="w-5 h-5" />
          </button>
          <button
            onClick={exportResults}
            className="flex items-center justify-center p-3 bg-[#b97a3a] text-white rounded-lg hover:bg-[#c98540] transition-colors"
            title="Export CSV"
          >
            <Download className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mt-6">
        {/* Mobile: Expandable filters */}
        <div className="sm:hidden">
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="flex items-center justify-between w-full p-3 text-left bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <span className="font-medium text-gray-700">Filters</span>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">
                {filteredResults.length} of {results.length} results
              </span>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${filtersExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          
          {filtersExpanded && (
            <div className="mt-3 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search results..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <select
                  value={filterSourceType}
                  onChange={(e) => setFilterSourceType(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Sources</option>
                  <option value="manual">Manual</option>
                  <option value="sqlserver">Imported</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="show_date">Show Date</option>
                  <option value="created_at">Date Created</option>
                  <option value="show_name">Show Name</option>
                  <option value="total_points">Total Points</option>
                  <option value="total_earnings">Total Earnings</option>
                </select>
              </div>
            </div>
          )}
        </div>
        
        {/* Desktop: Always visible filters */}
        <div className="hidden sm:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search results..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <select
              value={filterSourceType}
              onChange={(e) => setFilterSourceType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Sources</option>
              <option value="manual">Manual</option>
              <option value="sqlserver">Imported</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="show_date">Show Date</option>
              <option value="created_at">Date Created</option>
              <option value="show_name">Show Name</option>
              <option value="total_points">Total Points</option>
              <option value="total_earnings">Total Earnings</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <span className="text-sm text-gray-600">
              {filteredResults.length} of {results.length} results
            </span>
          </div>
        </div>
      </div>

      {/* Results List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading results...</p>
        </div>
      ) : sortedResults.length === 0 ? (
        <div className="text-center py-8">
          <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No results found</p>
        </div>
      ) : (
        <>
          {/* Mobile list */}
          <div className="sm:hidden space-y-2">

                         {/* Existing Results */}
             {sortedResults.map((result) => {
               const firstDate = result.result_data?.ShowDate || result.ShowDate || result.show_start_date || null;
               
                             // Handle new structure with draws array
              let events = [];
              if (result.result_data?.events && Array.isArray(result.result_data.events) && result.result_data.events.length > 0) {
                // New structure: flatten draws from all events
                result.result_data.events.forEach(event => {
                  if (event.draws && Array.isArray(event.draws)) {
                    event.draws.forEach(draw => {
                                            // Calculate total points and earnings from all contestants
                      const totalPoints = draw.contestants && Array.isArray(draw.contestants) 
                        ? draw.contestants.reduce((sum, c) => sum + (c.points || 0), 0) 
                        : 0;
                      const totalEarnings = draw.contestants && Array.isArray(draw.contestants) 
                        ? draw.contestants.reduce((sum, c) => sum + (c.earnings || 0), 0) 
                        : 0;
                      
                      events.push({
                         name: event.name || '',
                         eventDate: result.ShowDate || result.show_start_date || '',
                         drawNumber: draw.drawNumber || '',
                         timerTime: draw.timerTime || '',
                         penaltyTime: draw.penaltyTime || '',
                         finalTime: draw.finalTime || '',
                         timeStatus: draw.timeStatus || 'OK',
                         points: totalPoints,
                         earnings: totalEarnings,
                         placing: draw.rank1 || draw.rank2 || '',
                         contestants: draw.contestants || [],
                         format: event.format || ''
                       });
                    });
                  } else {
                    // Fallback for events without draws structure
                    events.push({
                      name: event.name || '',
                      eventDate: result.ShowDate || result.show_start_date || '',
                      drawNumber: event.drawNumber || '',
                      timerTime: event.timerTime || '',
                      penaltyTime: event.penaltyTime || '',
                      finalTime: event.finalTime || '',
                      timeStatus: event.timeStatus || 'OK',
                      points: event.points || 0,
                      earnings: event.earnings || 0,
                      placing: event.placing || '',
                      contestants: event.contestants || [],
                      format: event.format || ''
                    });
                  }
                });
              } else {
                // Fallback to empty event
                events = [{
                   name: '',
                   eventDate: '',
                   drawNumber: '',
                   timerTime: '',
                   penaltyTime: '',
                   finalTime: '',
                   timeStatus: 'OK',
                   points: 0,
                   earnings: 0,
                   contestants: [],
                   format: ''
                }];
              }
               return (
                                  <div key={`m-res-${result.id}`} className="bg-white border rounded-lg p-3">
                   <div className="flex items-center justify-between">
                     <div className="flex items-center flex-1">
                       <button
                         onClick={() => toggleShowExpansion(result.id)}
                         className="text-gray-500 hover:text-gray-700 transition-colors mr-2"
                         title={expandedShows.has(result.id) ? "Collapse events" : "Expand events"}
                       >
                         {expandedShows.has(result.id) ? (
                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                           </svg>
                         ) : (
                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                           </svg>
                         )}
                       </button>
                       <div className="text-sm font-semibold text-gray-900">
                         {result.result_data?.ShowName || result.ShowName || 'N/A'}
                         <span className="text-gray-500 font-normal ml-2">
                           {firstDate ? format(parseISO(firstDate), 'MM/dd/yy') : 'N/A'}
                         </span>
                       </div>
                     </div>
                     
                     {/* Action buttons on the same row */}
                     <div className="flex space-x-2">
                       {result.source_type === 'manual' ? (
                         <button
                           onClick={() => editResult(result)}
                           className="p-2 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                           aria-label="Edit"
                           title="Edit"
                         >
                           <Edit className="w-4 h-4" />
                         </button>
                       ) : (
                         <button
                           onClick={() => viewResult(result)}
                           className="p-2 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                           aria-label="View"
                           title="View"
                         >
                           <Eye className="w-4 h-4" />
                         </button>
                       )}
                       <button
                         onClick={() => confirmDelete(result)}
                         className="p-2 rounded text-red-600 hover:text-red-800 hover:bg-red-50"
                         aria-label="Delete"
                         title="Delete"
                       >
                         <Trash2 className="w-4 h-4" />
                       </button>
                     </div>
                   </div>
                   
                   {/* Events - only show when expanded */}
                   {expandedShows.has(result.id) && (
                     <div className="mt-1 space-y-2">
                       {events.map((event, idx) => (
                         <div key={`m-res-${result.id}-ev-${idx}`} className="bg-gray-50 rounded p-2">
                           <div className="text-sm font-semibold text-gray-900 mb-2 border-b border-gray-200 pb-1">
                             {event.name || 'Event'}
                             {event.format && (
                               <span className="text-xs text-gray-500 ml-2">({event.format})</span>
                             )}
                           </div>
                           <div className="grid grid-cols-2 gap-2 text-xs text-gray-700">
                             <div className="flex justify-between">
                               <span className="font-medium">Draw:</span>
                               <span className="font-mono">{event.position || 'N/A'}</span>
                             </div>
                             <div className="flex justify-between">
                               <span className="font-medium">Place:</span>
                               <span className="font-mono">{event.placing || 'N/A'}</span>
                             </div>
                             <div className="flex justify-between">
                               <span className="font-medium">Time:</span>
                               <span className="font-mono">{event.timerTime || 'N/A'}</span>
                             </div>
                             <div className="flex justify-between">
                               <span className="font-medium">Penalty:</span>
                               <span className="font-mono">{event.penaltyTime || '0.000'}</span>
                             </div>
                             <div className="flex justify-between">
                               <span className="font-medium">Final:</span>
                               <span className="font-mono">{event.finalTime || 'N/A'}</span>
                             </div>
                             <div className="flex justify-between">
                               <span className="font-medium">Status:</span>
                               <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(event.timeStatus || 'OK')}`}>
                                 {event.timeStatus || 'OK'}
                               </span>
                             </div>
                             <div className="flex justify-between">
                               <span className="font-medium">Points:</span>
                               <span>{(parseInt(event.points) || 0)} pts</span>
                             </div>
                             <div className="flex justify-between">
                               <span className="font-medium">Earnings:</span>
                               <span>${(parseFloat(event.earnings) || 0).toFixed(2)}</span>
                             </div>
                           </div>
                           {/* Contestants and Stock Info */}
                           {event.contestants && event.contestants.length > 0 && event.contestants.some(c => c.name) && (
                             <div className="text-xs text-gray-600 mt-2 pt-2 border-t border-gray-200">
                               <span className="font-medium">Contestants:</span> {event.contestants.filter(c => c.name).map(c => {
                                 const contestantInfo = c.name || 'Unknown';
                                 const positionInfo = c.position ? ` (${c.position})` : '';
                                 const stockInfo = c.stockName ? ` - ${c.stockName}` : '';
                                 return `${contestantInfo}${positionInfo}${stockInfo}`;
                               }).join(', ')}
                             </div>
                           )}
                         </div>
                       ))}
                     </div>
                   )}
                 </div>
                 );
               })}
          </div>

          {/* Desktop/Tablet table */}
          <div className="hidden sm:block bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
              <thead className="bg-gray-50">
                                 <tr>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">Event</th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Draw</th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Place</th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Time/Score</th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Penalty</th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Final</th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Status</th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Pts/$$$</th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16"></th>
                 </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* SQL Server Results Header */}
                {sqlServerResults.length > 0 && (
                  <tr className="bg-blue-50 border-b border-blue-200">
                    <td colSpan="9" className="px-6 py-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="text-lg font-semibold text-blue-900">Imported Results</h3>
                          <p className="text-sm text-blue-700 mt-1">
                            {sqlServerResults.length} show(s) ready to import. Click "Save" to import all results.
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => saveSqlServerResultsToDatabase()}
                            disabled={loading}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                          >
                            {loading ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                                 {/* SQL Server Results */}
                 {sqlServerResults.length > 0 && sqlServerResults.map((show, showIndex) => (
                  <React.Fragment key={`sql-${showIndex}`}>
                    {/* Show name row - spans all columns */}
                    <tr className="bg-blue-50 border-b border-gray-200">
                      <td colSpan="9" className="px-6 py-3">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center space-x-3">
                            <div className="text-lg font-semibold text-gray-900">
                              {show.showName || 'N/A'}
                              <span className="text-sm font-normal text-gray-500 ml-2">
                                {formatDate(show.showDate)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                              KCIRodeo
                            </span>
                            <span className="text-xs text-gray-500">
                              {show.events.length} event{show.events.length !== 1 ? 's' : ''}
                            </span>
                            <button
                              onClick={() => confirmDelete({ id: `sql-${showIndex}`, showName: show.showName, source_type: 'kcirodeo', showIndex })}
                              className="text-red-600 hover:text-red-800 text-sm"
                              title="Delete entire show"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    {/* Event rows */}
                    {show.events.map((event, eventIndex) => (
                      <tr key={`sql-${showIndex}-event-${eventIndex}`} className="hover:bg-gray-50 bg-blue-50">
                        <td className="px-6 py-3">
                          <div className="text-sm font-semibold text-gray-900 leading-tight">
                            {event.eventName || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-center">
                          <div className="text-sm text-gray-900">
                            {event.drawNumber || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-center">
                          <div className="text-sm text-gray-900">
                            {event.placing || 'N/A'}
                          </div>
                        </td>
                                                 <td className="px-6 py-3 text-center">
                           <div className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                             {event.timerTime || 'N/A'}
                           </div>
                         </td>
                         <td className="px-6 py-3 text-center">
                           <div className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                             {event.penaltyTime || '0.000'}
                           </div>
                         </td>
                         <td className="px-6 py-3 text-center">
                           <div className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                             {event.finalTime || 'N/A'}
                           </div>
                         </td>
                        <td className="px-6 py-3 text-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(event.timeStatus || 'OK')}`}>
                            {event.timeStatus || 'OK'}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <div className="text-center">
                            <div className="text-sm font-medium text-gray-900">
                              {event.points || 0} pts
                            </div>
                            <div className="text-xs text-gray-500">
                              ${(parseFloat(event.earnings) || 0).toFixed(2)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex items-center justify-center">
                            {/* Actions for individual events can go here if needed */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                
                {/* Existing Results */}
                {sortedResults.map((result) => {
                  return (
                  <React.Fragment key={result.id}>
                                         {/* Show name row - spans all columns */}
                     <tr className="border-b border-gray-200">
                       <td colSpan="9" className="px-6 py-3">
                         <div className="flex justify-between items-center">
                           <div className="flex items-center">
                             <button
                               onClick={() => toggleShowExpansion(result.id)}
                               className="mr-2 text-gray-500 hover:text-gray-700 transition-colors"
                               title={expandedShows.has(result.id) ? "Collapse events" : "Expand events"}
                             >
                               {expandedShows.has(result.id) ? (
                                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                 </svg>
                               ) : (
                                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                 </svg>
                               )}
                             </button>
                             <div>
                               <div className="text-lg font-semibold text-gray-900">
                                 {result.result_data?.ShowName}
                                 <span className="text-sm font-normal text-gray-500 ml-2">
                                   {formatDate(result.result_data?.ShowDate)}
                                 </span>
                               </div>
                               {/* Show ownership if different from current user */}
                               {result.owner_name && (
                                 <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded mt-1 inline-block">
                                   {result.owner_name}
                                 </div>
                               )}
                             </div>
                           </div>
                          <div className="flex items-center gap-4">
                            <span className={`text-xs px-2 py-1 rounded ${
                              result.source_type === 'manual' 
                                ? 'text-green-600 bg-green-100' 
                                : result.source_type === 'kcirodeo'
                                ? 'text-blue-600 bg-blue-100'
                                : 'text-gray-600 bg-gray-100'
                            }`}>
                              {result.source_type === 'manual' ? 'Manual' :
                               result.source_type === 'kcirodeo' ? 'Imported' : 'Unknown'}
                            </span>
                                {result.source_type === 'manual' ? (
                               <>
                                 <button
                                   onClick={() => editResult(result)}
                                   className="px-2 py-1 rounded text-xs text-blue-600 hover:text-blue-900 hover:bg-blue-50 border border-blue-200"
                                   title="Edit this show"
                                 >
                                   Edit
                                 </button>
                                 <button
                                   onClick={() => confirmDelete(result)}
                                   className="px-2 py-1 rounded text-xs text-red-600 hover:text-red-900 hover:bg-red-50 border border-red-200"
                                   title="Delete entire show"
                                 >
                                   Delete
                                 </button>
                               </>
                             ) : (
                               <>
                                 <button
                                   onClick={() => viewResult(result)}
                                   className="px-2 py-1 rounded text-xs text-blue-600 hover:text-blue-900 hover:bg-blue-50 border border-blue-200"
                                   title="View this show"
                                 >
                                   View
                                 </button>
                                 <button
                                   onClick={() => confirmDelete(result)}
                                   className="px-2 py-1 rounded text-xs text-red-600 hover:text-red-900 hover:bg-red-50 border border-red-200"
                                   title="Delete entire show"
                                 >
                                   Delete
                                 </button>
                               </>
                             )}
                          </div>
                        </div>
                      </td>
                    </tr>
                                        {/* Event rows */}
                    {expandedShows.has(result.id) && (
                      <>
                        {result.result_data?.events && result.result_data.events.length > 0 ? (
                          result.result_data.events.map((event, index) => {
                            // Handle new structure with draws array
                            if (event.draws && Array.isArray(event.draws) && event.draws.length > 0) {
                              // New structure: show each draw as a separate row
                              return event.draws.map((draw, drawIndex) => {
                                // Calculate total points and earnings from all contestants
                                const totalPoints = draw.contestants && Array.isArray(draw.contestants) 
                                  ? draw.contestants.reduce((sum, c) => sum + (c.points || 0), 0) 
                                  : 0;
                                const totalEarnings = draw.contestants && Array.isArray(draw.contestants) 
                                  ? draw.contestants.reduce((sum, c) => sum + (c.earnings || 0), 0) 
                                  : 0;
                                
                                return (
                                  <tr key={`${result.id}-event-${index}-draw-${drawIndex}`} className="hover:bg-gray-50">
                                    <td className="px-6 py-3">
                                      <div className="text-sm font-semibold text-gray-900 leading-tight">
                                        {event.name || 'N/A'}
                                      </div>
                                      {/* Contestants Info with Stock Names */}
                                      {draw.contestants && draw.contestants.length > 0 && (
                                        <div className="text-xs text-gray-600 mt-1">
                                          <span className="font-medium">Contestants:</span> {draw.contestants.map(c => {
                                            const contestantInfo = c.contestantName || 'Unknown';
                                            const positionInfo = c.position ? ` (${c.position})` : '';
                                            const stockInfo = c.stockName ? ` - ${c.stockName}` : '';
                                            return `${contestantInfo}${positionInfo}${stockInfo}`;
                                          }).join(', ')}
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-6 py-3 text-center">
                                      <div className="text-sm text-gray-900">
                                        {draw.drawNumber || 'N/A'}
                                      </div>
                                    </td>
                                    <td className="px-6 py-3 text-center">
                                      <div className="text-sm text-gray-900">
                                        {draw.rank1 || draw.rank2 || 'N/A'}
                                      </div>
                                    </td>
                                    <td className="px-6 py-3 text-center">
                                      <div className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                                        {formatScore(draw.timerTime)}
                                      </div>
                                    </td>
                                    <td className="px-6 py-3 text-center">
                                      <div className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                                        {draw.penaltyTime || '0.000'}
                                      </div>
                                    </td>
                                    <td className="px-6 py-3 text-center">
                                                                              <div className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                                        {draw.finalTime || 'N/A'}
                                      </div>
                                    </td>
                                    <td className="px-6 py-3 text-center">
                                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(draw.timeStatus || 'OK')}`}>
                                        {draw.timeStatus || 'OK'}
                                      </span>
                                    </td>
                                    <td className="px-6 py-3">
                                      <div className="text-center">
                                        <div className="text-sm font-medium text-gray-900">
                                          {totalPoints} pts
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          ${totalEarnings.toFixed(2)}
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-3">
                                      <div className="flex items-center justify-center">
                                        {/* Actions moved to show name row */}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              });
                            } else {
                              // Fallback for events without draws structure
                              return (
                                <tr key={`${result.id}-event-${index}`} className="hover:bg-gray-50">
                                  <td className="px-6 py-3">
                                    <div className="text-sm font-semibold text-gray-900 leading-tight">
                                      {event.name || 'N/A'}
                                    </div>
                                    {/* Event Format */}
                                    {event.format && (
                                      <div className="text-xs text-gray-600 mt-1">
                                        <span className="font-medium">Format:</span> {event.format}
                                      </div>
                                    )}
                                    {/* Stock Info */}
                                    {event.stockname && (
                                      <div className="text-sm text-gray-600 mt-1">
                                        <span className="font-medium">Stock:</span> {event.stockname}
                                      </div>
                                    )}
                                    {/* Contestants Info */}
                                    {event.contestants && event.contestants.length > 0 && (
                                      <div className="text-xs text-gray-600 mt-1">
                                        <span className="font-medium">Contestants:</span> {event.contestants.map(c => `${c.contestantName || c.name}${c.position ? ` (${c.position})` : ''}`).join(', ')}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-6 py-3 text-center">
                                    <div className="text-sm text-gray-900">
                                      {event.position || event.drawNumber || 'N/A'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-3 text-center">
                                    <div className="text-sm text-gray-900">
                                      {event.placing || 'N/A'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-3 text-center">
                                    <div className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                                      {formatScore(event.timerTime)}
                                    </div>
                                  </td>
                                  <td className="px-6 py-3 text-center">
                                    <div className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                                      {event.penaltyTime || '0.000'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-3 text-center">
                                    <div className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                                      {event.finalTime || 'N/A'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-3 text-center">
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(event.timeStatus || 'OK')}`}>
                                      {event.timeStatus || 'OK'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-3">
                                    <div className="text-center">
                                      <div className="text-sm font-medium text-gray-900">
                                        {event.points || 0} pts
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        ${(parseFloat(event.earnings) || 0).toFixed(2)}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-3">
                                    <div className="flex items-center justify-center">
                                      {/* Actions moved to show name row */}
                                    </div>
                                  </td>
                                </tr>
                              );
                            }
                          })
                        ) : (
                          // Single event result
                          <tr key={`${result.id}-single`} className="hover:bg-gray-50">
                            <td className="px-6 py-3">
                              <div className="text-sm font-semibold text-gray-900 leading-tight">
                                {result.first_event_name || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-3 text-center">
                              <div className="text-sm text-gray-900">
                                {result.result_data?.events?.[0]?.position || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-3 text-center">
                              <div className="text-sm text-gray-900">
                                {result.result_data?.events?.[0]?.placing || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-3 text-center">
                              <div className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                                {formatScore(result.result_data?.events?.[0]?.timerTime || result.first_score)}
                              </div>
                            </td>
                            <td className="px-6 py-3 text-center">
                              <div className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                                {result.result_data?.events?.[0]?.penaltyTime || '0.000'}
                              </div>
                            </td>
                            <td className="px-6 py-3 text-center">
                              <div className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                                {result.result_data?.events?.[0]?.finalTime || result.first_score || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-3 text-center">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(result.result_data?.events?.[0]?.timeStatus || 'OK')}`}>
                                {result.result_data?.events?.[0]?.timeStatus || 'OK'}
                              </span>
                            </td>
                            <td className="px-6 py-3">
                              <div className="text-center">
                                <div className="text-sm font-medium text-gray-900">
                                  {result.first_points || 0} pts
                                </div>
                                <div className="text-xs text-gray-500">
                                  ${(parseFloat(result.first_earnings) || 0).toFixed(2)}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-3">
                              <div className="flex items-center justify-center">
                                {/* Actions moved to show name row */}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </React.Fragment>
                );
                })}
              </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Add Result Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Add New Result</h2>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Show Information */}
                <div>
                  <h3 className="text-lg font-medium mb-2">Show Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Show Name</label>
                      <input
                        type="text"
                        placeholder="Enter show name"
                        value={newResult.show.name}
                        onChange={(e) => updateShow('name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={newResult.show.startDate}
                          onChange={(e) => updateShow('startDate', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Family Member Selection */}
                {hasFeature('family_plan') && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                      <User className="w-5 h-5 mr-2" />
                      Family Member
                    </h3>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">Assign to Family Member</label>
                      <select
                        value={newResult.family_member_id || 'primary'}
                        onChange={(e) => {
                          setNewResult(prev => ({
                            ...prev,
                            family_member_id: e.target.value
                          }));
                          setSelectedFamilyMemberForResult(e.target.value);
                        }}
                        className="w-full border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {familyMembers.map((member) => (
                          <option key={member.id} value={member.id}>
                            {member.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Events */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">Events</h3>
                    <button
                      onClick={addEvent}
                      className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                    >
                      Add Event
                    </button>
                  </div>
                  
                  {newResult.events.map((event, eventIndex) => (
                    <div key={eventIndex} className="border border-gray-200 rounded-lg p-4 mb-6">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-medium text-lg">Event {eventIndex + 1}</h4>
                        {newResult.events.length > 1 && (
                          <button
                            onClick={() => removeEvent(eventIndex)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {/* Event Information */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Event Name</label>
                          <input
                            type="text"
                            placeholder="Enter event name"
                            value={event.name}
                            onChange={(e) => updateEvent(eventIndex, 'name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        {/* Event Date removed to match imported structure */}
                      </div>

                      {/* Draws */}
                      <div className="ml-4">
                        <div className="flex justify-between items-center mb-3">
                          <h5 className="font-medium text-md">Draws</h5>
                          <button
                            onClick={() => addDraw(eventIndex)}
                            className="px-2 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                          >
                            Add Draw
                          </button>
                        </div>
                        
                        {(event.draws || []).map((draw, drawIndex) => (
                          <div key={drawIndex} className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
                            <div className="flex justify-between items-center mb-3">
                              <h6 className="font-medium">Draw {drawIndex + 1}</h6>
                              {((event.draws?.length || 0) > 1) && (
                                <button
                                  onClick={() => removeDraw(eventIndex, drawIndex)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                            
                            {/* Draw Information */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Draw Number</label>
                                <input
                                  type="text"
                                  placeholder="Enter draw number"
                                  value={draw.drawNumber}
                                  onChange={(e) => updateDraw(eventIndex, drawIndex, 'drawNumber', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Timer Time</label>
                                <input
                                  type="text"
                                  placeholder="Enter timer time"
                                  value={draw.timerTime}
                                  onChange={(e) => updateDraw(eventIndex, drawIndex, 'timerTime', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Penalty Time</label>
                                <input
                                  type="text"
                                  placeholder="Enter penalty time"
                                  value={draw.penaltyTime}
                                  onChange={(e) => updateDraw(eventIndex, drawIndex, 'penaltyTime', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Final Time</label>
                                <input
                                  type="text"
                                  placeholder="Enter final time"
                                  value={draw.finalTime}
                                  onChange={(e) => updateDraw(eventIndex, drawIndex, 'finalTime', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Time Status</label>
                                <select
                                  value={draw.timeStatus || 'OK'}
                                  onChange={(e) => updateDraw(eventIndex, drawIndex, 'timeStatus', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                  <option value="OK">OK</option>
                                  <option value="N/T">N/T</option>
                                  <option value="T/O">T/O</option>
                                  <option value="Vet">Vet</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Place</label>
                                <input
                                  type="text"
                                  placeholder="Enter place"
                                  value={draw.rank1}
                                  onChange={(e) => updateDraw(eventIndex, drawIndex, 'rank1', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              </div>
                            </div>

                            {/* Contestants */}
                            <div className="ml-4">
                              <div className="flex justify-between items-center mb-3">
                                <h6 className="font-medium text-sm">Contestants</h6>
                                <button
                                  onClick={() => addDrawContestant(eventIndex, drawIndex)}
                                  className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                >
                                  Add Contestant
                                </button>
                              </div>
                              
                              {draw.contestants.map((contestant, contestantIndex) => (
                                <div key={contestantIndex} className="border border-gray-200 rounded-lg p-3 mb-3 bg-white">
                                  <div className="flex justify-between items-center mb-2">
                                    <h6 className="font-medium text-sm">Contestant {contestantIndex + 1}</h6>
                                    {draw.contestants.length > 1 && (
                                      <button
                                        onClick={() => removeDrawContestant(eventIndex, drawIndex, contestantIndex)}
                                        className="text-red-600 hover:text-red-800"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                                    <div className="md:col-span-5">
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Contestant Name</label>
                                      <input
                                        type="text"
                                        placeholder="Enter contestant name"
                                        value={contestant.contestantName}
                                        onChange={(e) => updateDrawContestant(eventIndex, drawIndex, contestantIndex, 'contestantName', e.target.value)}
                                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Position</label>
                                      <input
                                        type="text"
                                        placeholder="Enter position"
                                        value={contestant.position}
                                        onChange={(e) => updateDrawContestant(eventIndex, drawIndex, contestantIndex, 'position', e.target.value)}
                                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Stock Name</label>
                                      <input
                                        type="text"
                                        placeholder="Enter stock name"
                                        value={contestant.stockName}
                                        onChange={(e) => updateDrawContestant(eventIndex, drawIndex, contestantIndex, 'stockName', e.target.value)}
                                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Points</label>
                                      <input
                                        type="number"
                                        placeholder="Enter points"
                                        value={contestant.points}
                                        onChange={(e) => updateDrawContestant(eventIndex, drawIndex, contestantIndex, 'points', e.target.value)}
                                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Earnings ($)</label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        placeholder="Enter earnings"
                                        value={contestant.earnings}
                                        onChange={(e) => updateDrawContestant(eventIndex, drawIndex, contestantIndex, 'earnings', e.target.value)}
                                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Points</label>
                    <input
                      type="number"
                      value={newResult.totalPoints}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Earnings</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newResult.totalEarnings}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>

                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <button
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addNewResult}
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading ? 'Adding...' : 'Add Result'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

             {/* Edit Result Modal */}
       {showEditForm && editingResult && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
           <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
             <div className="p-6">
               <div className="flex justify-between items-center mb-4">
                 <h2 className="text-xl font-semibold">Edit Result</h2>
                 <button
                   onClick={cancelEdit}
                   className="text-gray-400 hover:text-gray-600"
                 >
                   <X className="w-6 h-6" />
                 </button>
               </div>
               
               <div className="space-y-6">
                 {/* Show Information */}
                 <div>
                   <h3 className="text-lg font-medium mb-2">Show Information</h3>
                   <div className="space-y-4">
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Show Name</label>
                       <input
                         type="text"
                         placeholder="Enter show name"
                         value={newResult.show.name}
                         onChange={(e) => updateShow('name', e.target.value)}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                       />
                     </div>
                     <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                       <div>
                         <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                         <input
                           type="date"
                           value={newResult.show.startDate}
                           onChange={(e) => updateShow('startDate', e.target.value)}
                           className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                         />
                       </div>
                     </div>
                   </div>
                 </div>

                 {/* Family Member Selection */}
                 {hasFeature('family_plan') && (
                   <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                     <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                       <User className="w-5 h-5 mr-2" />
                       Family Member
                     </h3>
                     <div>
                       <label className="block text-sm font-medium text-blue-700 mb-1">Assign to Family Member</label>
                       <select
                         value={newResult.family_member_id || 'primary'}
                         onChange={(e) => setNewResult(prev => ({
                           ...prev,
                           family_member_id: e.target.value
                         }))}
                         className="w-full border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                       >
                         {familyMembers.map((member) => (
                           <option key={member.id} value={member.id}>
                             {member.name}
                           </option>
                         ))}
                       </select>
                     </div>
                   </div>
                 )}

                 {/* Events */}
                 <div>
                   <div className="flex justify-between items-center mb-4">
                     <h3 className="text-lg font-medium">Events</h3>
                     <button
                       onClick={addEvent}
                       className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                     >
                       Add Event
                     </button>
                   </div>
                   
                   {newResult.events.map((event, eventIndex) => (
                     <div key={eventIndex} className="border border-gray-200 rounded-lg p-4 mb-6">
                       <div className="flex justify-between items-center mb-4">
                         <h4 className="font-medium text-lg">Event {eventIndex + 1}</h4>
                         {newResult.events.length > 1 && (
                           <button
                             onClick={() => removeEvent(eventIndex)}
                             className="text-red-600 hover:text-red-800"
                           >
                             <X className="w-4 h-4" />
                           </button>
                         )}
                       </div>
                       
                       {/* Event Information */}
                       <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                         <div>
                           <label className="block text-sm font-medium text-gray-700 mb-1">Event Name</label>
                           <input
                             type="text"
                             placeholder="Enter event name"
                             value={event.name}
                             onChange={(e) => updateEvent(eventIndex, 'name', e.target.value)}
                             className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                           />
                         </div>
                         {/* Event Date removed to match imported structure */}
                       </div>

                       {/* Draws */}
                       <div className="ml-4">
                         <div className="flex justify-between items-center mb-3">
                           <h5 className="font-medium text-md">Draws</h5>
                           <button
                             onClick={() => addDraw(eventIndex)}
                             className="px-2 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                           >
                             Add Draw
                           </button>
                         </div>
                         
                         {event.draws.map((draw, drawIndex) => (
                           <div key={drawIndex} className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
                             <div className="flex justify-between items-center mb-3">
                               <h6 className="font-medium">Draw {drawIndex + 1}</h6>
                               {event.draws.length > 1 && (
                                 <button
                                   onClick={() => removeDraw(eventIndex, drawIndex)}
                                   className="text-red-600 hover:text-red-800"
                                 >
                                   <X className="w-4 h-4" />
                                 </button>
                               )}
                             </div>
                             
                             {/* Draw Information */}
                             <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                               <div>
                                 <label className="block text-sm font-medium text-gray-700 mb-1">Draw Number</label>
                                 <input
                                   type="text"
                                   placeholder="Enter draw number"
                                   value={draw.drawNumber}
                                   onChange={(e) => updateDraw(eventIndex, drawIndex, 'drawNumber', e.target.value)}
                                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                 />
                               </div>
                               <div>
                                 <label className="block text-sm font-medium text-gray-700 mb-1">Timer Time</label>
                                 <input
                                   type="text"
                                   placeholder="Enter timer time"
                                   value={draw.timerTime}
                                   onChange={(e) => updateDraw(eventIndex, drawIndex, 'timerTime', e.target.value)}
                                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                 />
                               </div>
                               <div>
                                 <label className="block text-sm font-medium text-gray-700 mb-1">Penalty Time</label>
                                 <input
                                   type="text"
                                   placeholder="Enter penalty time"
                                   value={draw.penaltyTime}
                                   onChange={(e) => updateDraw(eventIndex, drawIndex, 'penaltyTime', e.target.value)}
                                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                 />
                               </div>
                               <div>
                                 <label className="block text-sm font-medium text-gray-700 mb-1">Final Time</label>
                                 <input
                                   type="text"
                                   placeholder="Enter final time"
                                   value={draw.finalTime}
                                   onChange={(e) => updateDraw(eventIndex, drawIndex, 'finalTime', e.target.value)}
                                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                 />
                               </div>
                               <div>
                                 <label className="block text-sm font-medium text-gray-700 mb-1">Time Status</label>
                                 <select
                                   value={draw.timeStatus || 'OK'}
                                   onChange={(e) => updateDraw(eventIndex, drawIndex, 'timeStatus', e.target.value)}
                                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                 >
                                   <option value="OK">OK</option>
                                   <option value="N/T">N/T</option>
                                   <option value="T/O">T/O</option>
                                   <option value="Vet">Vet</option>
                                 </select>
                               </div>
                               <div>
                                 <label className="block text-sm font-medium text-gray-700 mb-1">Place</label>
                                 <input
                                   type="text"
                                   placeholder="Enter place"
                                   value={draw.rank1}
                                   onChange={(e) => updateDraw(eventIndex, drawIndex, 'rank1', e.target.value)}
                                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                 />
                               </div>
                             </div>

                             {/* Contestants */}
                             <div className="ml-4">
                               <div className="flex justify-between items-center mb-3">
                                 <h6 className="font-medium text-sm">Contestants</h6>
                                 <button
                                   onClick={() => addDrawContestant(eventIndex, drawIndex)}
                                   className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                 >
                                   Add Contestant
                                 </button>
                               </div>
                               
                               {draw.contestants.map((contestant, contestantIndex) => (
                                 <div key={contestantIndex} className="border border-gray-200 rounded-lg p-3 mb-3 bg-white">
                                   <div className="flex justify-between items-center mb-2">
                                     <h6 className="font-medium text-sm">Contestant {contestantIndex + 1}</h6>
                                     {draw.contestants.length > 1 && (
                                       <button
                                         onClick={() => removeDrawContestant(eventIndex, drawIndex, contestantIndex)}
                                         className="text-red-600 hover:text-red-800"
                                       >
                                         <X className="w-3 h-3" />
                                       </button>
                                     )}
                                   </div>
                                   
                                   <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                                     <div className="md:col-span-5">
                                       <label className="block text-xs font-medium text-gray-700 mb-1">Contestant Name</label>
                                       <input
                                         type="text"
                                         placeholder="Enter contestant name"
                                         value={contestant.contestantName}
                                         onChange={(e) => updateDrawContestant(eventIndex, drawIndex, contestantIndex, 'contestantName', e.target.value)}
                                         className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                       />
                                     </div>
                                     <div>
                                       <label className="block text-xs font-medium text-gray-700 mb-1">Position</label>
                                       <input
                                         type="text"
                                         placeholder="Enter position"
                                         value={contestant.position}
                                         onChange={(e) => updateDrawContestant(eventIndex, drawIndex, contestantIndex, 'position', e.target.value)}
                                         className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                       />
                                     </div>
                                     <div>
                                       <label className="block text-xs font-medium text-gray-700 mb-1">Stock Name</label>
                                       <input
                                         type="text"
                                         placeholder="Enter stock name"
                                         value={contestant.stockName}
                                         onChange={(e) => updateDrawContestant(eventIndex, drawIndex, contestantIndex, 'stockName', e.target.value)}
                                         className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                       />
                                     </div>
                                     <div>
                                       <label className="block text-xs font-medium text-gray-700 mb-1">Points</label>
                                       <input
                                         type="number"
                                         placeholder="Enter points"
                                         value={contestant.points}
                                         onChange={(e) => updateDrawContestant(eventIndex, drawIndex, contestantIndex, 'points', e.target.value)}
                                         className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                       />
                                     </div>
                                     <div>
                                       <label className="block text-xs font-medium text-gray-700 mb-1">Earnings ($)</label>
                                       <input
                                         type="number"
                                         step="0.01"
                                         placeholder="Enter earnings"
                                         value={contestant.earnings}
                                         onChange={(e) => updateDrawContestant(eventIndex, drawIndex, contestantIndex, 'earnings', e.target.value)}
                                         className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                       />
                                     </div>
                                   </div>
                                 </div>
                               ))}
                             </div>
                           </div>
                         ))}
                       </div>
                     </div>
                   ))}
                 </div>

                 {/* Totals */}
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">Total Points</label>
                     <input
                       type="number"
                       value={newResult.totalPoints}
                       readOnly
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                     />
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">Total Earnings</label>
                     <input
                       type="number"
                       step="0.01"
                       value={newResult.totalEarnings}
                       readOnly
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                     />
                   </div>
                 </div>

                 <div className="flex justify-end gap-2 pt-4">
                   <button
                     onClick={cancelEdit}
                     className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                   >
                     Cancel
                   </button>
                   <button
                     onClick={saveEditedResult}
                     disabled={loading}
                     className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                   >
                     {loading ? 'Saving...' : 'Save Changes'}
                   </button>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}

       {/* View Result Modal */}
       {showViewForm && viewingResult && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
           <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
             <div className="p-4 sm:p-6 lg:p-8">
               <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
                 <div className="flex items-center">
                   <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                   </svg>
                   <h2 className="text-2xl font-bold text-gray-900">View Result Details</h2>
                 </div>
                 <button
                   onClick={closeViewForm}
                   className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100"
                 >
                   <X className="w-6 h-6" />
                 </button>
               </div>
               
               {/* Read-only form content */}
               <div className="space-y-6">            
                 {/* Show Information */}
                 <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                   <h3 className="text-lg font-semibold text-green-900 mb-3 flex items-center">
                     <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                     </svg>
                     Show Information
                   </h3>
                   <div className="space-y-3">
                     <div className="flex items-center gap-3">
                       <span className="text-sm font-medium text-green-700">Show Name:</span>
                       <span className="text-lg font-semibold text-green-900 bg-white px-3 py-2 rounded-lg border border-green-200">
                         {newResult.show.name || 'N/A'}
                       </span>
                     </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                         <div className="flex items-center gap-3">
                           <span className="text-sm font-medium text-green-700">Date:</span>
                           <span className="text-sm text-green-900 bg-white px-2 py-1 rounded border">
                             {newResult.show.startDate ? format(new Date(newResult.show.startDate), 'MMM dd, yyyy') : 'N/A'}
                           </span>
                         </div>
                       </div>
                   </div>
                 </div>

                 {/* Events */}
                 <div>
                   <h3 className="text-lg font-medium mb-4">Events</h3>
                   
                   {newResult.events.map((event, index) => (
                     <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
                       {/* Event Name and Format */}
                       <div className="mb-4 pb-3 border-b border-gray-200">
                         <div className="flex justify-between items-center">
                           <h4 className="text-lg font-semibold text-gray-900">
                             {event.name || `Event ${index + 1}`}
                           </h4>
                         </div>
                       </div>
                       
                       {/* Handle new structure with draws array */}
                       {event.draws && Array.isArray(event.draws) && event.draws.length > 0 ? (
                         // New structure: show each draw
                         event.draws.map((draw, drawIndex) => {
                           return (
                             <div key={`draw-${drawIndex}`} className="border border-gray-200 rounded-lg p-4 mb-3 bg-white">
                               {/* Draw Header */}
                               <div className="mb-3 pb-2 border-b border-gray-200">
                                 <div className="flex justify-between items-center">
                                   <h5 className="text-md font-semibold text-gray-800">
                                     Draw #{draw.drawNumber || drawIndex + 1}
                                   </h5>
                                   <div className="flex items-center gap-2">
                                     <span className="text-sm font-medium text-gray-600">Place:</span>
                                     <span className="text-sm font-semibold text-gray-900 bg-gray-100 px-2 py-1 rounded border">
                                       {draw.rank1 || draw.rank2 || 'N/A'}
                                     </span>
                                   </div>
                                 </div>
                               </div>
                               
                               {/* Draw Data - 2 Columns */}
                               <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                 {/* Left Column: Time, Penalty, Final, Status */}
                                 <div className="space-y-3">
                                   <div className="flex justify-between items-center">
                                     <span className="text-sm font-medium text-gray-600">Time:</span>
                                     <span className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded border">
                                       {draw.timerTime || 'N/A'}
                                     </span>
                                   </div>
                                   <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                                     <span className="text-sm font-medium text-gray-600">Penalty:</span>
                                     <span className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded border">
                                       {draw.penaltyTime || '0.000'}
                                     </span>
                                   </div>
                                   <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                                     <span className="text-sm font-medium text-gray-600">Final:</span>
                                     <span className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded border">
                                       {draw.finalTime || 'N/A'}
                                     </span>
                                   </div>
                                   <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                                     <span className="text-sm font-medium text-gray-600">Status:</span>
                                     <span className={`text-xs px-2 py-1 rounded font-semibold ${getStatusColor(draw.timeStatus || 'OK')}`}>
                                       {draw.timeStatus || 'OK'}
                                     </span>
                                   </div>
                                 </div>
                                 
                                                                   {/* Right Column: Points, Earnings */}
                                  <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                      <span className="text-sm font-medium text-gray-600">Total Points:</span>
                                      <span className="text-lg font-semibold text-gray-900 bg-gray-50 px-3 py-2 rounded border">
                                        {draw.contestants && Array.isArray(draw.contestants) 
                                          ? draw.contestants.reduce((sum, c) => sum + (c.points || 0), 0) 
                                          : 0}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                                      <span className="text-sm font-medium text-gray-600">Total Earnings:</span>
                                      <span className="text-lg font-semibold text-green-700 bg-gray-50 px-3 py-2 rounded border">
                                        ${(draw.contestants && Array.isArray(draw.contestants) 
                                          ? draw.contestants.reduce((sum, c) => sum + (c.earnings || 0), 0) 
                                          : 0).toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                               </div>
                               
                               {/* Contestants Panel */}
                               {draw.contestants && draw.contestants.length > 0 && (
                                 <div className="mt-4 pt-3 border-t border-gray-200">
                                   <h6 className="text-sm font-semibold text-gray-700 mb-3">Contestants</h6>
                                   <div className="space-y-2">
                                     {draw.contestants.map((contestant, contestantIndex) => (
                                       <div key={contestantIndex} className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                                         <div className="flex justify-between items-start">
                                           <div className="flex-1">
                                             <div className="font-medium text-blue-900">
                                               {contestant.contestantName || contestant.name || 'Unknown'}
                                             </div>
                                             {contestant.position && (
                                               <div className="text-sm text-blue-700">
                                                 Position: {contestant.position}
                                               </div>
                                             )}
                                             {contestant.stockName && (
                                               <div className="text-sm text-blue-700">
                                                 Stock: {contestant.stockName}
                                               </div>
                                             )}
                                           </div>
                                           <div className="text-right">
                                             <div className="text-sm font-semibold text-blue-900">
                                               {contestant.points || 0} pts
                                             </div>
                                             <div className="text-sm text-green-700">
                                               ${(parseFloat(contestant.earnings) || 0).toFixed(2)}
                                             </div>
                                           </div>
                                         </div>
                                       </div>
                                     ))}
                                   </div>
                                 </div>
                               )}
                             </div>
                           );
                         })
                       ) : (
                         // Fallback for events without draws structure
                         <div className="space-y-3">
                           {/* Event Data - Desktop Layout (2 columns) + Mobile Layout */}
                           <div className="space-y-3">
                             {/* Desktop: Top Summary Row - Points & Earnings */}
                             <div className="hidden lg:grid grid-cols-2 gap-6 mb-4">
                               <div className="flex items-center justify-center">
                                 <span className="text-sm font-medium text-gray-600 mr-2">Points:</span>
                                 <span className="text-lg font-semibold text-gray-900 bg-white px-3 py-2 rounded border">
                                   {event.points || 0}
                                 </span>
                               </div>
                               <div className="flex items-center justify-center">
                                 <span className="text-sm font-medium text-gray-600 mr-2">Earnings:</span>
                                 <span className="text-lg font-semibold text-green-700 bg-white px-3 py-2 rounded border">
                                   ${(parseFloat(event.earnings) || 0).toFixed(2)}
                                 </span>
                               </div>
                             </div>
                             
                             {/* Desktop: Main Grid - 2 Columns */}
                             <div className="hidden lg:grid grid-cols-2 gap-6">
                               {/* Left Column: Time, Penalty, Final, Status */}
                               <div className="space-y-3">
                                 <div className="flex justify-between items-center">
                                   <span className="text-sm font-medium text-gray-600">Time:</span>
                                   <span className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border">
                                     {event.timerTime || 'N/A'}
                                   </span>
                                 </div>
                                 <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                                   <span className="text-sm font-medium text-gray-600">Penalty:</span>
                                   <span className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border">
                                     {event.penaltyTime || '0.000'}
                                   </span>
                                 </div>
                                 <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                                   <span className="text-sm font-medium text-gray-600">Final:</span>
                                   <span className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border">
                                     {event.finalTime || 'N/A'}
                                   </span>
                                 </div>
                                 <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                                   <span className="text-sm font-medium text-gray-600">Status:</span>
                                   <span className={`text-xs px-2 py-1 rounded font-semibold ${getStatusColor(event.timeStatus || 'OK')}`}>
                                     {event.timeStatus || 'OK'}
                                   </span>
                                 </div>
                               </div>
                               
                               {/* Right Column: Draw, Position, Stock */}
                               <div className="space-y-3">
                                 <div className="flex justify-between items-center">
                                   <span className="text-sm font-medium text-gray-600">Draw:</span>
                                   <span className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border">
                                     {event.drawNumber || 'N/A'}
                                   </span>
                                 </div>
                                 <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                                   <span className="text-sm font-medium text-gray-600">Position:</span>
                                   <span className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border">
                                     {event.position || 'N/A'}
                                   </span>
                                 </div>
                                 <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                                   <span className="text-sm font-medium text-gray-600">Stock:</span>
                                   <span className="text-sm text-gray-900 bg-white px-2 py-1 rounded border">
                                     {event.stockname || 'N/A'}
                                   </span>
                                 </div>
                               </div>
                             </div>
                             
                             {/* Mobile Layout */}
                             <div className="lg:hidden space-y-3">
                               {/* Row 1: Time, Penalty, Final, Status */}
                               <div className="grid grid-cols-4 gap-3">
                                 <div className="flex flex-col items-center">
                                   <span className="text-xs font-medium text-gray-600 mb-1">Time</span>
                                   <span className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border w-full text-center">
                                     {event.timerTime || 'N/A'}
                                   </span>
                                 </div>
                                 <div className="flex flex-col items-center">
                                   <span className="text-xs font-medium text-gray-600 mb-1">Penalty</span>
                                   <span className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border w-full text-center">
                                     {event.penaltyTime || '0.000'}
                                   </span>
                                 </div>
                                 <div className="flex flex-col items-center">
                                   <span className="text-xs font-medium text-gray-600 mb-1">Final</span>
                                   <span className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border w-full text-center">
                                     {event.finalTime || 'N/A'}
                                   </span>
                                 </div>
                                 <div className="flex flex-col items-center">
                                   <span className="text-xs font-medium text-gray-600 mb-1">Status</span>
                                   <span className={`text-xs px-2 py-1 rounded font-semibold ${getStatusColor(event.timeStatus || 'OK')} w-full text-center`}>
                                     {event.timeStatus || 'OK'}
                                   </span>
                                 </div>
                               </div>
                               
                               {/* Row 2: Draw, Points, Earnings */}
                               <div className="grid grid-cols-3 gap-3">
                                 <div className="flex flex-col items-center">
                                   <span className="text-xs font-medium text-gray-600 mb-1">Draw</span>
                                   <span className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border w-full text-center">
                                     {event.drawNumber || 'N/A'}
                                   </span>
                                 </div>
                                 <div className="flex flex-col items-center">
                                   <span className="text-xs font-medium text-gray-600 mb-1">Points</span>
                                   <span className="text-sm font-semibold text-gray-900 bg-white px-2 py-1 rounded border w-full text-center">
                                     {event.points || 0}
                                   </span>
                                 </div>
                                 <div className="flex flex-col items-center">
                                   <span className="text-xs font-medium text-gray-600 mb-1">Earnings</span>
                                   <span className="text-sm font-semibold text-green-700 bg-white px-2 py-1 rounded border w-full text-center">
                                     ${(parseFloat(event.earnings) || 0).toFixed(2)}
                                   </span>
                                 </div>
                               </div>
                               
                               {/* Row 3: Position and Stock */}
                               <div className="grid grid-cols-1 gap-3">
                                 <div className="flex flex-col items-center">
                                   <span className="text-xs font-medium text-gray-600 mb-1">Position</span>
                                   <span className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border w-full text-center">
                                     {event.position || 'N/A'}
                                   </span>
                                 </div>
                                 <div className="flex flex-col items-center">
                                   <span className="text-xs font-medium text-gray-600 mb-1">Stock</span>
                                   <span className="text-sm text-gray-900 bg-white px-2 py-1 rounded border w-full text-center">
                                     {event.stockname || 'N/A'}
                                   </span>
                                 </div>
                               </div>
                             </div>
                           </div>
                           
                           {/* Contestants Panel for events without draws */}
                           {event.contestants && event.contestants.length > 0 && (
                             <div className="mt-4 pt-3 border-t border-gray-200">
                               <h6 className="text-sm font-semibold text-gray-700 mb-3">Contestants</h6>
                               <div className="space-y-2">
                                 {event.contestants.map((contestant, contestantIndex) => (
                                   <div key={contestantIndex} className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                                     <div className="flex justify-between items-start">
                                       <div className="flex-1">
                                         <div className="font-medium text-blue-900">
                                           {contestant.contestantName || contestant.name || 'Unknown'}
                                         </div>
                                         {contestant.position && (
                                           <div className="text-sm text-blue-700">
                                             Position: {contestant.position}
                                           </div>
                                         )}
                                         {contestant.stockName && (
                                           <div className="text-sm text-blue-700">
                                             Stock: {contestant.stockName}
                                           </div>
                                         )}
                                       </div>
                                       <div className="text-right">
                                         <div className="text-sm font-semibold text-blue-900">
                                           {contestant.points || 0} pts
                                         </div>
                                         <div className="text-sm text-green-700">
                                           ${(parseFloat(contestant.earnings) || 0).toFixed(2)}
                                         </div>
                                       </div>
                                     </div>
                                   </div>
                                 ))}
                               </div>
                             </div>
                           )}
                         </div>
                       )}
                     </div>
                   ))}
                 </div>

                 {/* Totals */}
                 <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                   <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center">
                     <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                     </svg>
                     Summary Totals
                   </h3>
                   <div className="flex items-center gap-6">
                     <div className="flex items-center gap-3">
                       <span className="text-sm font-medium text-purple-700">Total Points:</span>
                       <span className="text-xl font-bold text-purple-900 bg-white px-4 py-2 rounded-lg border border-purple-200">
                         {newResult.totalPoints || 0}
                       </span>
                     </div>
                     <div className="flex items-center gap-3">
                       <span className="text-sm font-medium text-purple-700">Total Earnings:</span>
                       <span className="text-xl font-bold text-green-700 bg-white px-4 py-2 rounded-lg border border-green-200">
                         ${(parseFloat(newResult.totalEarnings) || 0).toFixed(2)}
                       </span>
                     </div>
                   </div>
                 </div>

                 <div className="flex justify-end gap-2 pt-6">
                   <button
                     onClick={closeViewForm}
                     className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                   >
                     Close
                   </button>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}

      {/* Import from Association Modal */}
      {showImportForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Import from Association</h2>
                <button
                  onClick={() => setShowImportForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Association Name</label>
                  <input
                    type="text"
                    placeholder="Enter association name"
                    value={importData.association_name}
                    onChange={(e) => setImportData(prev => ({ ...prev, association_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Results Data (JSON)</label>
                  <textarea
                    placeholder="Paste JSON data from association..."
                    value={JSON.stringify(importData.results_data, null, 2)}
                    onChange={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        setImportData(prev => ({ ...prev, results_data: Array.isArray(parsed) ? parsed : [] }));
                      } catch (error) {
                        // Invalid JSON, keep as is
                      }
                    }}
                    rows={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  />
                </div>

                <div className="text-sm text-gray-600">
                  <p><strong>Expected JSON format:</strong></p>
                  <pre className="bg-gray-100 p-2 rounded mt-2 text-xs">
{`[
  {
    "unique_identifier": "result_123",
    "association": "Association Name",
    "show": "Show Name",
    "show_date": "2024-03-15",
    "event": "Barrel Race",
    "draw_number": "123",
    "stock": "Horse Name",
    "time_score": "15.234",
    "penalty": 0,
    "final_time_score": "15.234",
    "status": "Completed",
    "earnings": 500.00,
    "points": 100,
    "notes": "Optional notes",
    "source_type": "association",
    "created_date": "2024-03-15T10:00:00Z"
  }
]`}
                  </pre>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <button
                    onClick={() => setShowImportForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={importFromAssociation}
                    disabled={loading || !importData.association_name || importData.results_data.length === 0}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Importing...' : 'Import Results'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showSqlServerForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Import Results</h2>
                <button
                  onClick={() => {
                    setShowSqlServerForm(false);
                    setSelectedKCIRShow(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Family Member Selection */}
                {familyAssociations.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {familyAssociations.length === 1 ? 'Import From' : 'Select an Association to Import From'}
                    </label>
                        <select
                          value={selectedFamilyMemberId}
                          onChange={(e) => handleFamilyMemberChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {familyAssociations.map(a => (
                            <option key={a.id} value={String(a.id)}>
                              {a.familyMemberName} - {a.associationName}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                        {familyAssociations.length === 1 
                          ? 'Using your association credentials for importing'
                          : 'Choose which family member\'s association credentials to use for importing'
                        }
                      </p>
                  </div>
                )}

                {/* Show Selection */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">Show Selection</h3>
                    <button
                      onClick={selectedKCIRShow ? () => setSelectedKCIRShow(null) : fetchKCIRShowsForResults}
                      className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      {selectedKCIRShow ? 'Clear Show' : 'Select Show'}
                    </button>
                  </div>
                  {!sqlServerData.associationKey ? (
                    <p className="text-sm text-gray-600">
                      Please select an association first to choose a show.
                    </p>
                  ) : selectedKCIRShow ? (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex justify-between items-start">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
                          <div>
                            <span className="text-sm font-medium text-blue-700">Show Name:</span>
                            <p className="text-blue-900 font-semibold">{selectedKCIRShow.showName}</p>
                          </div>

                          {selectedKCIRShow.showDate && (
                            <div>
                              <span className="text-sm font-medium text-blue-700">Date:</span>
                              <p className="text-blue-900">{formatDate(selectedKCIRShow.showDate)}</p>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => setSelectedKCIRShow(null)}
                          className="text-gray-500 hover:text-gray-700 ml-4"
                          title="Remove show selection"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600">
                      Select a specific show to import results from.
                    </p>
                  )}
                </div>

                

                {/* No Results Message */}
                {sqlServerResults.length === 0 && !sqlServerLoading && selectedKCIRShow && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                    <p className="text-gray-600">No results found for the selected show.</p>
                  </div>
                )}

                {/* Loading State */}
                {sqlServerLoading && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-gray-600">Fetching results...</p>
                  </div>
                )}

                <div className="flex justify-center gap-2 pt-4">
                  <button
                    onClick={() => {
                      fetchSqlServerResults();
                    }}
                    disabled={sqlServerLoading || !sqlServerData.associationKey || !sqlServerData.memberKey || !sqlServerData.memberPass || !selectedKCIRShow}
                    className="px-4 py-2 bg-[#8b6b3d] text-white rounded-lg hover:bg-[#a17a4a] disabled:opacity-50"
                  >
                    {sqlServerLoading ? 'Fetching...' : (!selectedKCIRShow ? 'Select Show First' : 'Fetch')}
                  </button>
                  <button
                    onClick={() => {
                      setShowSqlServerForm(false);
                      setSelectedKCIRShow(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>

                {/* Results Display Section (moved below action buttons for linear flow) */}
                {sqlServerResults.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-blue-900">Results Found</h3>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => saveSqlServerResultsToDatabase()}
                          disabled={loading}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                        >
                          {loading ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-blue-700 mb-3">
                      {sqlServerResults.length} show(s) ready to import. Click "Save" to import all results.
                    </p>
                    
                    {/* Simplified Results List: Show only event names */}
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {sqlServerResults.map((show, showIndex) => (
                        <div key={`modal-sql-${showIndex}`} className="bg-white border rounded-lg p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="text-sm font-semibold text-gray-900">
                                {show.showName}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-500">
                                {show.events.length} event{show.events.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>
                          {/* Show only event names */}
                          <div className="mt-1 space-y-1">
                            {show.events.map((event, idx) => (
                              <div key={`modal-sql-${showIndex}-ev-${idx}`} className="text-xs text-gray-700">
                                <span className="font-medium">{event.eventName}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                  </div>
            </div>
          </div>
        </div>
      )}

      {/* KCIRodeo Show Selection Modal for Results */}
      {showKCIRShowsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Select Show</h3>
              <button onClick={() => setShowKCIRShowsModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto" style={{ maxHeight: '60vh' }}>
              {kcirShowsLoading ? (
                <div className="text-center text-gray-600">Loading shows...</div>
              ) : (kcirShows && kcirShows.length > 0 ? (
                <div className="space-y-2">
                  {kcirShows.map((show) => (
                    <div key={show.showId} className="p-3 border rounded hover:bg-gray-50 cursor-pointer" onClick={() => { setSelectedKCIRShow(show); setShowKCIRShowsModal(false); }}>
                      <div className="font-medium">{show.showName}</div>
                      <div className="text-sm text-gray-600">{formatDate(show.showDate)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-600">No shows available.</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Delete</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700">
                  Are you sure you want to delete this show?
                </p>
                {deletingResult.source_type === 'kcirodeo' && (
                  <p className="text-xs text-gray-500 mt-1">
                    This will remove the show from the current display only.
                  </p>
                )}
                {deletingResult.source_type === 'manual' && (
                  <p className="text-xs text-gray-500 mt-1">
                    This will permanently delete the show and all its events.
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeletingResult(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const isTempSqlResult = typeof deletingResult.id === 'string' && deletingResult.id.startsWith('sql-');
                    if (isTempSqlResult) {
                      const showIndex = Number.isInteger(deletingResult.showIndex)
                        ? deletingResult.showIndex
                        : parseInt(deletingResult.id.replace('sql-', ''), 10);
                      if (!Number.isNaN(showIndex)) {
                      deleteSqlServerShow(showIndex);
                      }
                      setShowDeleteConfirm(false);
                      setDeletingResult(null);
                    } else {
                      deleteResult(deletingResult.id);
                      setShowDeleteConfirm(false);
                      setDeletingResult(null);
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function for ordinal suffixes
function getOrdinalSuffix(num) {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) {
    return "st";
  }
  if (j === 2 && k !== 12) {
    return "nd";
  }
  if (j === 3 && k !== 13) {
    return "rd";
  }
  return "th";
}